import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { AUTOMATION_QUERY_KEYS } from '../../../../queries/Automation/queryKeys';
import { useUpdateAutomationStepMutation } from '../../../../queries/Automation/mutations/steps/useUpdateAutomationStepMutation';
import type { StaleScheduleErrorWithRepair } from './usePublishAutomation';

type RefreshStatus = 'idle' | 'working' | 'done' | 'partial-failure';

interface UseRefreshStaleSchedulesOptions {
	/**
	 * Required. Always invalidated as `relatedTemplateIds` on each repair PUT so the
	 * root template's React Query cache refetches before validation re-runs.
	 */
	rootTemplateId: string;
	impersonationContext?: Api.IImpersonationContext;
	/**
	 * Fired after the refresh loop finishes (success or partial failure). The host uses
	 * this to trigger a hard reload on success — see RefreshTimingBanner's call site for
	 * the reasoning (React Query structural sharing + useAutomationFlow's cache layers
	 * make in-place re-derivation brittle).
	 */
	onComplete?: (result: { failedStepIds: string[] }) => void;
}

interface UseRefreshStaleSchedulesReturn {
	status: RefreshStatus;
	completedCount: number;
	totalCount: number;
	failedStepIds: string[];
	refresh: (repairs: StaleScheduleErrorWithRepair[]) => Promise<void>;
	reset: () => void;
}

/**
 * Reads the freshest persisted step from the React Query cache so a concurrent edit
 * isn't clobbered when we PUT the corrected schedule. Returns the cached step if
 * found, otherwise the snapshot captured by the validator.
 *
 * Template queries are keyed with the impersonation account/user id appended, so the
 * lookup uses getQueriesData to match all impersonation variants.
 */
function readFreshStep(
	queryClient: ReturnType<typeof useQueryClient>,
	ownerTemplateId: string,
	stepId: string,
	fallback: Api.IAutomationStep
): Api.IAutomationStep {
	const matches = queryClient.getQueriesData<Api.IAutomationTemplate>({
		queryKey: AUTOMATION_QUERY_KEYS.template(ownerTemplateId),
	});

	for (const [, template] of matches) {
		const draft = template?.draft;
		const fresh = draft?.steps?.find(step => step.id === stepId);
		if (fresh) {
			return fresh;
		}
	}

	return fallback;
}

/**
 * One-click refresh of stale switch-branch child schedules.
 *
 * Each `StaleScheduleErrorWithRepair` carries a `repair` payload computed by the
 * validator. This hook walks them in order — TanStack Query's mutation scope
 * (`AUTOMATION_STEP_MUTATION_SCOPE`) already serializes step mutations app-wide,
 * so a plain sequential await loop is equivalent in timing to firing them in
 * parallel and is easier to reason about.
 *
 * Per-iteration, the persisted step payload is re-read from the React Query cache
 * to avoid stomping a concurrent edit; if the freshest copy already has the
 * normalized criteria (e.g. the user manually re-saved between banner-show and
 * click), the iteration short-circuits and counts as a success.
 */
export function useRefreshStaleSchedules({
	rootTemplateId,
	impersonationContext,
	onComplete,
}: UseRefreshStaleSchedulesOptions): UseRefreshStaleSchedulesReturn {
	const queryClient = useQueryClient();
	const updateStep = useUpdateAutomationStepMutation();

	const [status, setStatus] = useState<RefreshStatus>('idle');
	const [completedCount, setCompletedCount] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const [failedStepIds, setFailedStepIds] = useState<string[]>([]);

	const reset = useCallback(() => {
		setStatus('idle');
		setCompletedCount(0);
		setTotalCount(0);
		setFailedStepIds([]);
	}, []);

	const refresh = useCallback(
		async (repairs: StaleScheduleErrorWithRepair[]) => {
			if (repairs.length === 0) {
				return;
			}

			const relatedTemplateIds = [rootTemplateId];

			setStatus('working');
			setTotalCount(repairs.length);
			setCompletedCount(0);
			setFailedStepIds([]);

			const failed: string[] = [];

			for (const error of repairs) {
				const repair = error.repair;
				const freshStep = readFreshStep(
					queryClient,
					repair.ownerTemplateId,
					repair.persistedStep.id,
					repair.persistedStep
				);

				// Skip if the cache already has the corrected criteria (e.g. the user re-saved
				// manually between banner display and click). Count as success.
				if (freshStep.schedule?.criteria === repair.normalizedSchedule.criteria) {
					setCompletedCount(prev => prev + 1);
					continue;
				}

				try {
					await updateStep.mutateAsync({
						templateId: repair.ownerTemplateId,
						stepType: repair.stepType,
						stepId: freshStep.id,
						step: { ...freshStep, schedule: repair.normalizedSchedule },
						relatedTemplateIds,
						impersonationContext,
					});
					setCompletedCount(prev => prev + 1);
				} catch {
					failed.push(freshStep.id);
				}
			}

			setFailedStepIds(failed);
			setStatus(failed.length === 0 ? 'done' : 'partial-failure');
			onComplete?.({ failedStepIds: failed });
		},
		[queryClient, updateStep, rootTemplateId, impersonationContext, onComplete]
	);

	return useMemo(
		() => ({ status, completedCount, totalCount, failedStepIds, refresh, reset }),
		[status, completedCount, totalCount, failedStepIds, refresh, reset]
	);
}
